import {
  useCurrentCloudProvider,
  isGivenDateExpired,
  useAccountId,
} from '@labrav/common'
import {
  LabraToolTipV2,
  OfferCard,
  VerticalDivider,
} from '@labrav/react-components'
import clsx from 'clsx'
import { useTimeZone } from '../../../common/utils/hooks/useTimeZone'
import { dateAndTimeConversion } from '../../../common/utils/dateAndTimeConversion'
import { useStyles } from './OffersAttention.styles'
import { Avatar } from '@mui/material'
import { getCloudImg } from '../RevenueOverviewChart/RevenueOverviewChart'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEnvelopeDot,
  faEnvelopeOpen,
} from '@fortawesome/pro-regular-svg-icons'
import { useSelector } from '../../../store/hooks'
import { Overview } from '../../../modules/flyoutOverview/types'
import { OverviewEmptyState } from '../OverviewEmptyState/OverviewEmptyState'
import OverviewEmptyStateImage from '../../../common/images/overviewEmptyState.png'
import { useNavigate, useParams } from 'react-router-dom'
export const OffersAttention = () => {
  const { timeZone } = useTimeZone()
  const { classes } = useStyles()
  const navigate = useNavigate()
  const { cloud: routeCloud, accountId: routeAccountId } = useParams<{
    cloud?: string
    accountId?: string
  }>()
  const defaultAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || defaultAccountId
  const selectedCloud = useCurrentCloudProvider()

  const goToOfferDetail = (
    productId?: string,
    labraOfferId?: string,
    offerType?: string,
    labraResaleAuthId?: string,
    cloud?: string
  ) => {
    if (offerType === 'CPPO') {
      const cloudPath = cloud?.toLowerCase() || selectedCloud.toLowerCase()
      const channelOfferPath =
        routeCloud && accountId
          ? `/flyout/${cloudPath}/${accountId}/authorization/${labraResaleAuthId}/channel-offers/${labraOfferId}/detail`
          : `/flyout/authorization/${labraResaleAuthId}/channel-offers/${labraOfferId}/detail`
      labraOfferId && navigate(channelOfferPath)
    }

    if (offerType === 'PRIVATE_OFFER') {
      const cloudPath = cloud?.toLowerCase() || selectedCloud.toLowerCase()
      const privateOfferPath =
        routeCloud && accountId
          ? `/flyout/${cloudPath}/${accountId}/products-listing/${productId}/private-offers/${labraOfferId}/details`
          : `/flyout/products-listing/${productId}/private-offers/${labraOfferId}/details`
      productId && labraOfferId && navigate(privateOfferPath)
    }
  }

  const { offersNeedingAttention } = useSelector<Overview>(
    state => state.flyoutOverview?.overview
  )

  const isOffersNeedingAttentionDataEmpty =
    !offersNeedingAttention || offersNeedingAttention.length === 0

  return (
    <>
      {isOffersNeedingAttentionDataEmpty ? (
        <OverviewEmptyState
          imgUrl={OverviewEmptyStateImage}
          containerHeight={36.25}
          imageHeight={12}
          imageWidth={16.75}
          description="No offers require your attention right now."
        />
      ) : (
        <div className={classes.root} data-testid={'offer-attention'}>
          {offersNeedingAttention?.map((offerItem, index) => {
            const isLastRow = index === offersNeedingAttention?.length - 1
            const isExpired = isGivenDateExpired(
              offerItem?.expiryDate,
              timeZone
            )
            const { modifiedDate } = dateAndTimeConversion(
              offerItem?.expiryDate,
              timeZone
            )
            const { dateString } = dateAndTimeConversion(
              offerItem?.offerViewedAt,
              timeZone
            )
            const descriptionSection = (
              <div
                className={clsx(
                  classes.offerExpiration,
                  isExpired ? classes.expiredOffer : ''
                )}
                data-testid={'card-description'}
              >
                {isExpired ? 'Expired on' : 'Expiring on'} {modifiedDate}
              </div>
            )
            const rightSection = (
              <>
                {offerItem?.salesBy && (
                  <>
                    <LabraToolTipV2
                      description={`Sales rep: ${offerItem?.salesBy}`}
                      tooltipPlacement="bottom"
                      tooltipPlacmentGap="-5"
                      className={classes.tooltipWrapper}
                    >
                      <Avatar
                        className={classes.avatar}
                        data-testid={'sales-avatar'}
                      >
                        {offerItem?.salesBy?.charAt(0).toUpperCase()}
                      </Avatar>
                    </LabraToolTipV2>
                    <VerticalDivider height="24px" />
                  </>
                )}
                <img
                  src={getCloudImg(offerItem?.cloud)}
                  data-testid={`icon1-${1}`}
                  style={{ width: '24px', height: '24px' }}
                />
                {offerItem?.offerStatus === 'OFFER_EMAIL_SENT' && (
                  <LabraToolTipV2
                    description={'Offer not viewed.'}
                    tooltipPlacement="bottom"
                    className={classes.tooltipWrapper}
                  >
                    <FontAwesomeIcon
                      icon={faEnvelopeDot}
                      style={{
                        width: '18px',
                        height: '20px',
                        color: '#6A777A',
                        padding: '3px 4px',
                      }}
                      data-testid={'offer-sent'}
                    />
                  </LabraToolTipV2>
                )}
                {offerItem?.offerStatus === 'OFFER_VIEWED' && (
                  <LabraToolTipV2
                    description={`Most recently viewed by ${offerItem?.offerViewedBy} at ${dateString}`}
                    tooltipPlacement="bottom"
                    className={classes.tooltipWrapper}
                  >
                    <FontAwesomeIcon
                      icon={faEnvelopeOpen}
                      style={{
                        width: '16px',
                        height: '20px',
                        color: '#6A777A',
                        padding: '2px 4px',
                      }}
                      data-testid={'offer-viewed'}
                    />
                  </LabraToolTipV2>
                )}
              </>
            )
            return (
              <div
                className={isLastRow ? classes.lastRow : ''}
                data-testid={'offer-card-row'}
              >
                <OfferCard
                  logoTooltip={offerItem?.companyName}
                  name={offerItem?.offerName}
                  onArrowClick={() =>
                    goToOfferDetail(
                      offerItem?.awsProductId,
                      offerItem?.id,
                      offerItem?.offerType,
                      offerItem?.labraResaleAuthId,
                      offerItem?.cloud
                    )
                  }
                  logo={offerItem?.companyLogo}
                  description={descriptionSection}
                  rightSideSection={rightSection}
                  actionTooltip="View offer details"
                  isLastCard={isLastRow}
                />
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
